import React, { useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import intersection from 'lodash/intersection';
import { Box } from 'components';
import { MultiSelect2, Select2, Text, HeaderText } from '@el8/vital';
import TimezoneSelect from 'components/selects/TimezoneSelect';
import { getBookingInfo, updateBookingInfo } from 'modules/booking';
import { usePhysicians } from 'modules/physician';
import { usePractice } from 'modules/practice';
import { formatPhone, formatTimezone, logEvent } from 'utils';
import { getAppointmentTypes } from 'modules/appointmentType/appointmentTypeSelectors';
import useUrlQueryParamsSync from 'utils/hooks/useUrlQueryParamsSync';
import PracticeSchedule from '../PracticeSchedule/PracticeSchedule';

import styles from './PracticeHeader.less';

interface OwnProps {
  practiceId: number;
}

interface PhysicianItem {
  label: string;
  value: number;
}

interface AppointmentTypeItem {
  label: string;
  value: number;
}

const PracticeHeader = ({ practiceId }: OwnProps): JSX.Element => {
  const dispatch = useDispatch();
  const { locationPhysiciansWithHypotheticalAvailability } = useSelector(getBookingInfo);
  const [practice] = usePractice(practiceId);
  const { name, settings } = practice;
  const { contactEmail, phone } = settings;
  const addressLine: string = settings.practiceAddress.replace('\n', ', ');

  const [physicians] = usePhysicians(practiceId);
  const appointmentTypes = useSelector(getAppointmentTypes);

  const {
    selectedAppointmentTypeIds,
    selectedPhysicianId,
    timezone: selectedTimezone,
  } = useSelector(getBookingInfo);

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isAvailabilityOnBkSiteEnabled = window.el8Globals.FEATURES.AvailabilityOnBkSite;
  const isSchedulingTimeZoneEnabled = window.el8Globals.FEATURES.SchedulingTimeZone;

  const handleEmailClick = (): void => {
    logEvent('practice email contacted', {
      'practice email': practice.settings.contactEmail,
      'practice id': practiceId,
    });
  };

  const handlePhoneClick = (): void => {
    logEvent('call practice phone', {
      'practice id': practiceId,
    });
  };

  const handleSelectPhysician = (physicianId: number): void => {
    dispatch(updateBookingInfo({ selectedPhysicianId: physicianId }));

    if (physicianId && physicians) {
      const { firstName, lastName } = physicians.find(({ id }): boolean => id === physicianId);
      logEvent('apply provider filter', {
        'number of physicians': physicians.length,
        'physician id': physicianId,
        'physician name': `${firstName} ${lastName}`,
      });
    }
  };

  const handleSelectAppointmentType = (selectedItems: number[]): void => {
    dispatch(updateBookingInfo({ selectedAppointmentTypeIds: selectedItems }));
  };

  const appointmentTypeOptions = useMemo((): AppointmentTypeItem[] => {
    if (!appointmentTypes) return [];
    return appointmentTypes
      .sort((a, b) => a.sequence - b.sequence)
      .map(
        (apptType): AppointmentTypeItem => ({
          label: apptType.name,
          value: apptType.id,
        }),
      );
  }, [appointmentTypes]);

  const physicianOptions = useMemo((): PhysicianItem[] => {
    if (!physicians) return [];
    return [
      { label: 'All providers', value: null },
      ...physicians
        .filter((physician): boolean => {
          return (
            !locationPhysiciansWithHypotheticalAvailability ||
            locationPhysiciansWithHypotheticalAvailability?.includes(physician.id)
          );
        })
        .map(({ id, firstName, lastName, credentials }): PhysicianItem => {
          return {
            label: `${firstName} ${lastName}, ${credentials}`,
            value: id,
          };
        }),
    ];
  }, [locationPhysiciansWithHypotheticalAvailability, physicians]);

  const appointmentTypeIds = useMemo(() => {
    return appointmentTypes.map((apptType) => apptType.id);
  }, [appointmentTypes]);

  useEffect(() => {
    if (selectedAppointmentTypeIds === null) {
      const appointmentTypeParam = new URLSearchParams(window.location.search).get(
        'appointment_types',
      );
      const appointmentTypeParamIds = appointmentTypeParam
        ? appointmentTypeParam.split(',').map(Number)
        : [];
      const validAppointmentTypeParamIds = intersection(
        appointmentTypeParamIds,
        appointmentTypeIds,
      );
      const initialAppointmentTypeIds = validAppointmentTypeParamIds?.length
        ? validAppointmentTypeParamIds
        : appointmentTypeIds;
      dispatch(
        updateBookingInfo({
          selectedAppointmentTypeIds: initialAppointmentTypeIds,
        }),
      );
    }
  }, [appointmentTypeIds, dispatch, selectedAppointmentTypeIds]);

  useUrlQueryParamsSync([
    { param: 'physician_id', value: selectedPhysicianId },
    { param: 'appointment_types', value: selectedAppointmentTypeIds?.join(',') },
  ]);

  const renderPracticeTimezoneOrSelect = (): JSX.Element | null => {
    const { timezone: practiceTimezone } = practice;
    const shouldShowTimezone = localTimezone !== practiceTimezone;

    if (isSchedulingTimeZoneEnabled) {
      return (
        <div className={cn(isAvailabilityOnBkSiteEnabled && styles.selectContainer)}>
          <TimezoneSelect
            floatingLabel="Timezone"
            isSearchable
            onChange={(value: string): void => {
              dispatch(updateBookingInfo({ timezone: value }));
            }}
            value={selectedTimezone}
            style={{ maxWidth: '100%' }}
            enableEllipsis
          />
        </div>
      );
    }

    if (shouldShowTimezone) {
      return (
        <Box
          className={cn(styles.tz, !isAvailabilityOnBkSiteEnabled && styles.marginLeft)}
          align="center"
        >
          {formatTimezone(practiceTimezone)}
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      <HeaderText
        align="center"
        className={styles.practiceName}
        legacyVariant="legacyTitleText"
        sizing="heading-lg"
        tag="h1"
      >
        {name}
      </HeaderText>
      <div className={styles.practiceInfo}>
        <Text tag="div" align="center" weight="medium" className={styles.practiceContactInfo}>
          <div>{addressLine}</div>
          <Box justify="center">
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} onClick={handleEmailClick}>
                {contactEmail}
              </a>
            )}
            {contactEmail && phone && <span className={styles.separator} />}
            {phone && (
              <a
                className={styles.phoneLink}
                href={`tel:+1-${formatPhone(phone)}`}
                onClick={handlePhoneClick}
              >
                {formatPhone(phone)}
              </a>
            )}
          </Box>
        </Text>
        <PracticeSchedule practiceId={practiceId} />
        <Text tag="div" align="center" className={styles.practiceDescription}>
          {settings.practiceInfo}
        </Text>
      </div>

      <div className={styles.filters}>
        <Box style={{ gap: '8px', flex: 'no-wrap' }}>
          <div
            className={cn(
              isAvailabilityOnBkSiteEnabled ? styles.selectContainer : styles.providerSelect,
            )}
          >
            <Select2
              floatingLabel="Provider"
              items={physicianOptions}
              onChange={handleSelectPhysician}
              placeholder="Select provider"
              enableEllipsis
              style={{ maxWidth: '100%' }}
              value={selectedPhysicianId}
              isSearchable
            />
          </div>
          {isAvailabilityOnBkSiteEnabled && (
            <MultiSelect2
              floatingLabel="Appointment Type(s)"
              onChange={handleSelectAppointmentType}
              value={selectedAppointmentTypeIds || []}
              items={appointmentTypeOptions}
              enableEllipsis
              enableSelectAll
              width={200}
              isSearchable
            />
          )}
          {renderPracticeTimezoneOrSelect()}
        </Box>
      </div>
    </>
  );
};

export default PracticeHeader;
